import { useEffect, useRef } from 'react'
import type { ActionSummaryData } from '../../services/recommendations_api'

/** Maximum time to poll for summary generation before giving up (5 minutes) */
const POLLING_TIMEOUT_MS = 5 * 60 * 1000

interface UseRecommendationsPollingParams {
  /** Current summary data */
  summary: ActionSummaryData | null | undefined
  /** Function to refetch the summary */
  refetch: () => void
  /** Optional user ID for polling key (prevents stale polls when userId changes) */
  userId?: number
}

/**
 * Hook that polls for summary updates while status is 'GENERATING'.
 *
 * Polls every 3 seconds until:
 * - Status changes from 'GENERATING'
 * - 5 minute timeout is reached
 */
export function useRecommendationsPolling({
  summary,
  refetch,
  userId,
}: UseRecommendationsPollingParams): void {
  // Track when polling began without restarting the effect on each write.
  const pollingStartTimeRef = useRef<number | null>(null)

  // Poll every 3 seconds while generating, with 5-minute timeout
  useEffect(() => {
    if (summary?.status !== 'GENERATING') {
      pollingStartTimeRef.current = null
      return
    }

    if (pollingStartTimeRef.current === null) {
      pollingStartTimeRef.current = Date.now()
    }

    const interval = window.setInterval(() => {
      const pollingStartTime = pollingStartTimeRef.current

      if (
        pollingStartTime !== null &&
        Date.now() - pollingStartTime > POLLING_TIMEOUT_MS
      ) {
        console.warn(
          `Action summary generation polling timed out after ${POLLING_TIMEOUT_MS / 1000 / 60} minutes`
        )
        window.clearInterval(interval)
        return
      }

      void refetch()
    }, 3000)

    return () => window.clearInterval(interval)
  }, [summary?.status, refetch, userId])
}
